import { utf8_to_b64 } from '../../app/base64';
import _ from 'lodash';
import { protectedResources } from '../../authConfig';
import { SetupInstance } from '../../app/msal-account-helpers';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { PropertyFunctions } from "./property-functions";
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import { useLocal } from './../environment';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class PropertyAPI {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiProperty.scopes.read
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.baseUrl = useLocal ?
            "http://localhost:8080/api/property" :
            "https://apim.ianrobertson.co.uk/api/property/v2";

        this.accessTokenService = new AccessTokenHelper();
    }

    static listeners = [];

    static properties = [];

    getPropertyFromCache = id => {
        return _.find(PropertyAPI.properties, p => p.id === id);
    }

    addPropertyToCache = property => {
        const properties = PropertyAPI.properties;
        const existingPropertyIndex = _.findIndex(properties, p => p.id === property.id);
        
        if (existingPropertyIndex === -1) {
            properties.push(property);
        } else {
            properties.splice(existingPropertyIndex, 1, property);
        }

        PropertyAPI.properties = properties;
    }

    addPropertiesToCache = properties => {
        for (const property of Object.entries(properties).map(p => p[1])) {
            this.addPropertyToCache(property);
        }
    }

    static clearCache = () => {
        PropertyAPI.properties = [];
    };

    listenForUpdates(updateName, subscriberCallback) {

        const listener = _.find(PropertyAPI.listeners, l => l.name === updateName);
        if (!listener) {
            PropertyAPI.listeners.push({ name: updateName, callback: subscriberCallback });
        }
        else {
            // replace the whole listener...
            const listenerIndex = _.findIndex(PropertyAPI.listeners, l => l.name === updateName);
            PropertyAPI.listeners.splice(listenerIndex, 1); // Remove current version
            PropertyAPI.listeners.splice(listenerIndex, 0, { name: updateName, callback: subscriberCallback }); // Insert new version in its place
        }
    }

    getTopN = n => {
        const size = Number.isFinite(Number(n)) ? Number(n) : 10;

        return polly()
            .logger(error => {
                console.error("An error occurred in getTopN", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    throw new Error("Login interaction in-progress");
                }

                let accessTokenResponse;
                try {
                    accessTokenResponse = await this.accessTokenService.getAccessToken(
                        this.instance,
                        this.inProgress,
                        this.accessTokenRequest
                    );
                } catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                        // Let global auth handling (AuthGate / AccessTokenHelper) manage redirects
                    }
                    console.log(error);
                    throw error;
                }

                if (!accessTokenResponse) {
                    throw new Error("No access token");
                }

                const headers = new Headers();
                const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                headers.append("Authorization", bearer);

                const options = {
                    method: "GET",
                    headers: headers
                };

                const response = await fetch(`${this.baseUrl}/top/${size}`, options);

                if (!response.ok) {
                    throw response;
                }

                const data = await response.json();

                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));

                this.addPropertiesToCache(result);
                return result;
            });
    };

    find = searchTerm => {

        return polly()
            .logger(error => {
                console.error("An error occurred in find", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    const searchResult = [];

                    for (const property of PropertyAPI.properties) {

                        let match = false;

                        for (const memberName in property) {
                            // Skip if the value is null or undefined (loose comparison on purpose)
                            if (property[memberName] == null) {
                                continue;
                            }
                    
                            let member = property[memberName].toString();

                            if (member.search(new RegExp(searchTerm, "i")) > -1) {
                                match = true;
                                break;
                            }
                        }

                        if (match) {
                            searchResult.push(property);
                        }
                    }

                    if (searchResult.length === 0) {

                        console.log(`Searching property items using Search Term '${searchTerm}'`);

                        var url = `${this.baseUrl}/find?searchTerm=${utf8_to_b64(searchTerm)}`

                        console.log("Search Url", url);

                        if (this.inProgress !== InteractionStatus.None) {
                            return reject("Login interaction in-progress");
                        }

                        this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                            .then(accessTokenResponse => {

                                if (!!!accessTokenResponse) {
                                    return reject("No access token");
                                }

                                //console.log("Searching properties, using token:", accessTokenResponse.idToken);

                                const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                                const headers = new Headers();
                                headers.append("Authorization", bearer);
                                headers.append("Content-Type", "application/json");

                                const options = {
                                    method: "GET",
                                    headers: headers
                                };

                                fetch(url, options)
                                    .then(response => {
                                        if (response.ok) {

                                            response.json()
                                                .then(data => {

                                                    const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));

                                                    if (!!!result && result.length < 1) {
                                                        console.log("No results found for search:", searchTerm);
                                                        return reject("No results found");
                                                    }

                                                    this.addPropertiesToCache(result);

                                                    return resolve(Object.assign([], [].concat(searchResult, result)));
                                                })
                                        }
                                        else {
                                            reject(response);
                                        }
                                    })
                                    .catch(error => {
                                        console.log(error);
                                        reject(error)
                                    });
                            })
                            .catch((error) => {
                                if (error instanceof InteractionRequiredAuthError) {
                                    // Let centralized auth handling manage redirects
                                }
                                console.log(error);
                                reject(error)
                            });
                    }
                });
            })


    };

    get = id => {
        return polly()
            .logger(error => {
                console.error("An error occurred in get id =>", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    const property = this.getPropertyFromCache(id);
                    if (property) {
                        resolve(property);
                        return;
                    }

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const headers = new Headers();
                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            headers.append("Authorization", bearer);

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            fetch(`${this.baseUrl}/${id}`, options)
                                .then(response => response.json())
                                .then(data => {

                                    const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));

                                    PropertyAPI.clearCache(); // Invalidate cache after update
                                    this.addPropertyToCache(result);
                                    return resolve(result);
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });
                });
            });

    };

    upsertProperty = propertyDoc => {

        const newPropDoc = _.cloneDeep(propertyDoc);
        delete newPropDoc.property.landlordOrOwner;

        if (newPropDoc.property.photoGallery && newPropDoc.property.photoGallery.length === 1) {
            newPropDoc.property.photoGallery[0].isPrimaryImage = true;
        }

        if (newPropDoc.property.photoGallery) {
            for (const image of newPropDoc.property.photoGallery) {
                image.path = PropertyFunctions.formatBlobStorageGalleryString(image.path, newPropDoc.id, image.name);
                delete image.name;
            }
        }

        // Normalise room-level media gallery paths so we never send data URLs
        // to the backend. Mirror the property-level gallery behaviour by
        // converting each media item into a stable blob path under
        //   {propertyId}/Rooms/{roomId}/{imageName}
        if (newPropDoc.property.rooms && Array.isArray(newPropDoc.property.rooms)) {
            for (const room of newPropDoc.property.rooms) {
                if (!room || !Array.isArray(room.mediaGallery)) continue;

                const roomId = room.roomId || room.id || room._id || room.key;
                if (!roomId) continue;

                for (const image of room.mediaGallery) {
                    if (!image) continue;
                    const imageName = (image.name || '').split(':')[0];
                    if (!imageName) continue;

                    image.path = `${newPropDoc.id}/Rooms/${roomId}/${imageName}`;
                    delete image.name;
                }
            }
        }

        return polly()
            .logger(error => {
                console.error("An error occurred in upsertProperty", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {

                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            console.log(`Upserting property '${newPropDoc.id}', using token:`, accessTokenResponse.idToken);

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const clean = cleanUpJsonForTransport(newPropDoc);
                            const body = JSON.stringify(clean);

                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: body
                            };

                            fetch(`${this.baseUrl}/${newPropDoc.id}`, options)
                                .then(response => {
                                    if (response.ok) {

                                        response.json()
                                            .then(data => {

                                                const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));
                                                this.addPropertyToCache(result);
                                                return resolve(result);
                                            })
                                    }
                                    else {
                                        return reject(response);
                                    }
                                })
                                .catch(error => {
                                    console.log(error);
                                    reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            reject(error)
                        });

                });

            });


    };

    createProperty = propertyModel => {

        return polly()
            .logger(error => {
                console.error("An error occurred in createProperty", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            console.log("Creating property, using token:", accessTokenResponse.idToken);

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(propertyModel)
                            };

                            fetch(`${this.baseUrl}/CreateProperty`, options)
                                .then(response => {
                                    if (response.ok) {

                                        response.json()
                                            .then(data => {

                                                const result = cleanUpJsonForTransport(Object.assign({}, hydrateObjects(data)));

                                                PropertyAPI.clearCache(); // Invalidate cache after create
                                                this.addPropertyToCache(result);

                                                const listener = _.find(PropertyAPI.listeners, l => l.name === "propertyUpdate");
                                                if (listener) {
                                                    listener.callback(result);
                                                }
                                                resolve(result);
                                            })
                                    }
                                    else {
                                        return reject(response);
                                    }
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });

                });
            });


    };

    bulkCreateProperties = newProperties => {

        return polly()
            .logger(error => {
                console.error("An error occurred in bulkCreateProperties", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            console.log("Creating properties, using token:", accessTokenResponse.idToken);

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "POST",
                                headers: headers,
                                body: JSON.stringify(newProperties)
                            };

                            fetch(`${this.baseUrl}/BulkCreateProperties`, options)
                                .then(response => {
                                    if (response.ok) {

                                        response.json()
                                            .then(data => {

                                                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));

                                                PropertyAPI.clearCache(); // Invalidate cache after bulk create
                                                for (const propertyDoc of result) {
                                                    this.addPropertyToCache(propertyDoc);
                                                }
                                                return resolve(result);
                                            })
                                    }
                                    else {
                                        return reject(response.json()); // TODO: Should get validation errors back here as a JSON array/data... not handled by UI. Might need notifications etc.
                                    }
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });

                });
            });
    };
}